import { ProjectStatus } from "@lifeat/shared/projects/ProjectStatus";
import {
    ArchiveProjectRequest,
    CreateNewProjectRequest,
    CreateNewProjectResponse,
    DeleteProjectRequest,
    UpdateProjectColorRequest,
    UpdateProjectDescriptionRequest,
    UpdateProjectEmojiRequest,
    UpdateProjectEndDateRequest,
    UpdateProjectStartDateRequest,
    UpdateProjectStatusRequest,
    UpdateProjectTitleRequest,
} from "@lifeat/shared/projects/ProjectsFunctionContracts";
import {
    validateCreateNewProjectRequest,
    validateProjectDescription,
    validateProjectTitle,
} from "@lifeat/shared/projects/ProjectsUtil";
import { isValidHexColor, isValidISODateString, isValidUnicodeEmoji } from "@lifeat/shared/util/StringUtil";
import { logError } from "@service/ConsoleLoggerService";
import { callMonolith } from "@service/ServerlessFunctionsService";

export class ProjectsService {
    static async createNewProject(
        createProjectRequest: CreateNewProjectRequest,
    ): Promise<CreateNewProjectResponse | void> {
        const validationResult = validateCreateNewProjectRequest({
            title: createProjectRequest.title,
            emoji: createProjectRequest.emoji,
            color: createProjectRequest.color,
        });
        if (validationResult.isValid === false) {
            logError(`Project creation request is invalid: ${validationResult.reason}`);
            throw new Error(validationResult.reason);
        }

        try {
            const payload: CreateNewProjectRequest = {
                title: createProjectRequest.title,
                emoji: createProjectRequest.emoji,
                color: createProjectRequest.color,
            };
            return await callMonolith("create-new-project", payload);
        } catch (err) {
            logError(`Error creating project: ${err}`);
            throw err;
        }
    }

    static async updateProjectTitle(projectId: string, title: string): Promise<void> {
        const validationResult = validateProjectTitle(title);
        if (validationResult.isValid === false) {
            logError(`Project title is invalid: ${validationResult.reason}`);
            throw new Error(validationResult.reason);
        }

        try {
            const payload: UpdateProjectTitleRequest = {
                projectId: projectId,
                title: title,
            };
            await callMonolith("update-project-title", payload);
        } catch (err) {
            logError(`Error updating project title: ${err}`);
            throw err;
        }
    }

    static async updateProjectDescription(projectId: string, description: string): Promise<void> {
        const validationResult = validateProjectDescription(description);
        if (validationResult.isValid === false) {
            logError(`Project description is invalid: ${validationResult.reason}`);
            throw new Error(validationResult.reason);
        }

        try {
            const payload: UpdateProjectDescriptionRequest = {
                projectId: projectId,
                description: description,
            };
            await callMonolith("update-project-description", payload);
        } catch (err) {
            logError(`Error updating project description: ${err}`);
            throw err;
        }
    }

    static async updateProjectEmoji(projectId: string, emoji: string): Promise<void> {
        if (!isValidUnicodeEmoji(emoji)) {
            throw new Error("Project emoji is invalid");
        }

        try {
            const payload: UpdateProjectEmojiRequest = {
                projectId: projectId,
                emoji: emoji,
            };

            await callMonolith("update-project-emoji", payload);
        } catch (err) {
            logError(`Error updating project emoji: ${err}`);
            throw err;
        }
    }

    static async updateProjectColor(projectId: string, color: string): Promise<void> {
        if (!isValidHexColor(color)) {
            throw new Error("Project color is invalid");
        }

        try {
            const payload: UpdateProjectColorRequest = {
                projectId: projectId,
                color: color,
            };
            await callMonolith("update-project-color", payload);
        } catch (err) {
            logError(`Error updating project color: ${err}`);
            throw err;
        }
    }

    static async updateProjectStatus(projectId: string, status: ProjectStatus): Promise<void> {
        try {
            const payload: UpdateProjectStatusRequest = {
                projectId: projectId,
                status: status,
            };

            await callMonolith("update-project-status", payload);
        } catch (err) {
            logError(`Error updating project status: ${err}`);
            throw err;
        }
    }

    static async updateProjectStartDate(projectId: string, startDate: string | null): Promise<void> {
        if (startDate !== null && !isValidISODateString(startDate)) {
            throw new Error(`Project start date is invalid: ${startDate}`);
        }
        try {
            const payload: UpdateProjectStartDateRequest = {
                projectId: projectId,
                startDate: startDate,
            };
            await callMonolith("update-project-start-date", payload);
        } catch (err) {
            logError(`Error updating project start date: ${err}`);
            throw err;
        }
    }

    static async updateProjectEndDate(projectId: string, endDate: string | null): Promise<void> {
        if (endDate !== null && !isValidISODateString(endDate)) {
            throw new Error(`Project end date is invalid: ${endDate}`);
        }
        try {
            const payload: UpdateProjectEndDateRequest = {
                projectId: projectId,
                endDate: endDate,
            };
            await callMonolith("update-project-end-date", payload);
        } catch (err) {
            logError(`Error updating project end date: ${err}`);
            throw err;
        }
    }

    static async deleteProject(projectId: string): Promise<void> {
        try {
            const payload: DeleteProjectRequest = {
                projectId: projectId,
            };
            await callMonolith("delete-project", payload);
        } catch (err) {
            logError(`Error deleting project: ${err}`);
            throw err;
        }
    }

    static async archiveProject(projectId: string): Promise<void> {
        try {
            const payload: ArchiveProjectRequest = {
                projectId: projectId,
            };
            await callMonolith("archive-project", payload);
        } catch (err) {
            logError(`Error archiving project: ${err}`);
            throw err;
        }
    }
}
